import { flow } from 'fp-ts/function';
import { compact, keyBy, map, negate, overSome } from 'lodash/fp';
import { parse, stringify } from 'query-string';
import { createSelector, Selector } from 'reselect';

import {
    Amount,
    BookingEnginePaymentCardRequirement,
    convertGrossAmountToSimpleAmount,
    createZeroAmount,
    isPositiveAmount,
    MultiCurrencyUntaxedAmount,
    ZERO_AMOUNT,
} from '@mews-distributor/common';
import { PricingMode } from '@mews/enums';
import { getStringProp, omitFalsyValues } from '@mews/fp';
import { isRecaptchaServiceInitialized } from '@mews/framework';
import { isPciProxyInitialized } from '@mews/framework/payments';
import { composeProps } from '@mews/framework/redux';

import {
    CHECKOUT_DOCUMENT_TYPES,
    QUERY_PARAM_GA4_LINKER,
    QUERY_PARAM_HOTEL_ID,
    QUERY_PARAM_PAYMENT_CARD_ID,
    QUERY_PARAM_PROPERTY_RETURN_URL,
    QUERY_PARAM_RESERVATION_GROUP_ID,
    QUERY_PARAM_RESERVATION_IDS,
} from '../../../../constants';
import { getCurrencyCode, getLanguageCode } from '../../../../modules/app/selectors';
import { getPlatformPrimaryUrl } from '../../../../modules/configuration/selectors';
import { convertAmount } from '../../../../modules/entities/exchangeRates/viewSelectors';
import {
    getFlatImmediatelySettledAmounts,
    getFlatImmediatelySettledAmountsWithPreauthorizations,
    getReservationPrices,
    getReservationsTotalAmountInPgaCurrency,
    getReservationsTotalAmounts,
} from '../../../../modules/entities/reservationPrices/selectors';
import { ImmediateReservationPrice, ReservationPrice } from '../../../../modules/entities/reservationPrices/types';
import {
    currentReservationGroupIdPropsExtender,
    getCurrentReservationGroupId,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
} from '../../../../modules/entities/reservations/selectors';
import { hasServiceTypesDocuments } from '../../../../modules/entities/serviceDocuments/selectors';
import { getEnterpriseServiceId } from '../../../../modules/entities/services/selectors';
import { localizeCurrencyValue } from '../../../../modules/globalization/selectors';
import { getSelectedHotelId } from '../../../../modules/resourceOrder/selectors';
import { getSelectedEnterprisePaymentCardRequirement } from '../../../../selectors/views/selectedEnterpriseConfiguration';
import { hasSelectedEnterprisePaymentGateway } from '../../../../selectors/views/selectedEnterprisePaymentConfiguration';
import { ApplicationState } from '../../../../types';
import { WithReservationGroupId } from '../../../../types/selectors/selectorPropTypes';
import { getImmediatelySettledAmounts } from '../../../../utils/settlements/getImmediatelySettledAmounts';
import { sumAmounts } from '../checkoutAmounts';
import { hasReservationsPrices } from '../selectors';

import { isImmediatePriceFull, reservationPricesRequirePaymentCard } from './utils';

interface CommonPageUrlProps {
    enterpriseId: string;
    languageCode: string;
    paymentCardId?: string;
    reservationGroupId: string;
    reservationIds: string[];
    search: Location['search'];
    propertyReturnUrl: string | null;
    returnUrl: string;
    ga4Linker: string | null;
}

const navigatorPaymentRequestPath = '/navigator/payment-requests/detail/';
const navigatorCardAuthorizationPath = '/navigator/card-authorization/detail/';

const getReturnUrl = ({
    reservationGroupId,
    reservationIds,
    enterpriseId,
    paymentCardId,
    search,
    languageCode,
    returnUrl,
    propertyReturnUrl,
    ga4Linker,
}: CommonPageUrlProps) => {
    const newSearch = {
        ...parse(search),
        ...omitFalsyValues({
            [QUERY_PARAM_GA4_LINKER]: ga4Linker,
            [QUERY_PARAM_HOTEL_ID]: enterpriseId,
            [QUERY_PARAM_RESERVATION_GROUP_ID]: reservationGroupId,
            [QUERY_PARAM_RESERVATION_IDS]: reservationIds.join(','),
            [QUERY_PARAM_PAYMENT_CARD_ID]: paymentCardId,
            [QUERY_PARAM_PROPERTY_RETURN_URL]: propertyReturnUrl != null ? window.btoa(propertyReturnUrl) : null,
            language: languageCode,
        }),
    };

    return window.btoa(`${returnUrl}?${stringify(newSearch)}`);
};

const getReturnUrlFromProps = (_state: ApplicationState, props: CommonPageUrlProps) => getReturnUrl(props);

export const getPaymentRequestPageUrl = createSelector(
    getStringProp('paymentRequestId'),
    getStringProp('languageCode'),
    getStringProp('currencyCode'),
    getStringProp('paymentMethod'),
    getPlatformPrimaryUrl,
    getReturnUrlFromProps,
    (paymentRequestId, language, currency, paymentMethod, platformPrimaryUrl, returnUrl) =>
        `${platformPrimaryUrl}${navigatorPaymentRequestPath}${paymentRequestId}?${stringify({
            returnUrl,
            language,
            ccy: currency,
            paymentMethod,
        })}`
);

export const getCardAuthorizationPageUrl = createSelector(
    getStringProp('paymentCardId'),
    getStringProp('languageCode'),
    getPlatformPrimaryUrl,
    getReturnUrlFromProps,
    (paymentCardId, language, platformPrimaryUrl, returnUrl) =>
        `${platformPrimaryUrl}${navigatorCardAuthorizationPath}${paymentCardId}?${stringify({
            returnUrl,
            language,
        })}`
);

type AmountSelector = (state: ApplicationState, props: WithReservationGroupId) => Amount[];
const getConvertedSettledAmount = (amountSelector: AmountSelector) => (state: ApplicationState) => {
    const reservationGroupId = getCurrentReservationGroupId(state);
    const amounts = amountSelector(state, { reservationGroupId });
    const settledAmount = sumAmounts(state, { amounts });

    if (settledAmount === ZERO_AMOUNT) {
        const currency: string = getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});
        return { ...settledAmount, currency };
    }
    return settledAmount;
};

export const getImmediatelySettledTotalAmount = getConvertedSettledAmount(getFlatImmediatelySettledAmounts);
export const getImmediatelySettledTotalAmountWithPreauthorizations = getConvertedSettledAmount(
    getFlatImmediatelySettledAmountsWithPreauthorizations
);

const getReservationsTotalAmount = getConvertedSettledAmount(getReservationsTotalAmounts);

const getReservationsTotalAmountInTargetCurrency = (state: ApplicationState) => {
    const totalAmount = getReservationsTotalAmount(state);
    const targetCurrencyCode = getCurrencyCode(state);
    const enterpriseId = getSelectedHotelId(state);

    const targetCurrencyTotalAmount =
        targetCurrencyCode != null
            ? convertAmount(state, {
                  amount: totalAmount,
                  targetCurrencyCode,
                  enterpriseId,
              })
            : null;

    return targetCurrencyTotalAmount;
};

export const getReservationGroupTotalAmountInPaymentGatewayCurrency = (state: ApplicationState) => {
    const reservationGroupId = getCurrentReservationGroupId(state);
    const reservationGroupTotalAmounts = getReservationsTotalAmountInPgaCurrency(state);
    const currencyCode =
        (getCurrencyCode(state) ?? '') || getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});

    const amount = reservationGroupTotalAmounts?.[reservationGroupId];

    return convertGrossAmountToSimpleAmount(amount ?? createZeroAmount(currencyCode));
};

export const getCurrentReservationGroupPaymentGatewayCurrencyCode = (state: ApplicationState) => {
    const reservationGroupId = getCurrentReservationGroupId(state);
    const reservationGroupTotalAmounts = getReservationsTotalAmountInPgaCurrency(state);
    return reservationGroupTotalAmounts?.[reservationGroupId]?.currency;
};

export const getReservationsTotalMultiCurrencyAmount = createSelector(
    getReservationsTotalAmount,
    getReservationsTotalAmountInTargetCurrency,
    (totalAmount, targetCurrencyTotalAmount) =>
        flow(compact, keyBy<Amount>('currency'))([totalAmount, targetCurrencyTotalAmount])
);

export const getReservationsSimpleTotalAmount = createSelector(
    getReservationsTotalAmount,
    getReservationsTotalAmountInTargetCurrency,
    (totalAmount, targetCurrencyTotalAmount) =>
        convertGrossAmountToSimpleAmount(targetCurrencyTotalAmount ?? totalAmount)
);

const convertToGrossEnterpriseCurrencyValue =
    (currencyValueSelector: Selector<ApplicationState, MultiCurrencyUntaxedAmount>) => (state: ApplicationState) => {
        const currencyValue = currencyValueSelector(state);
        const currencyCode = getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});
        const languageCode = getLanguageCode(state);

        return localizeCurrencyValue(state, {
            // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
            languageCode,
            currencyValue,
            optionalCurrencyCode: currencyCode,
            pricingMode: PricingMode.Gross,
        });
    };

export const getReservationsGrossTotalInPropertyCurrency = convertToGrossEnterpriseCurrencyValue(
    getReservationsTotalMultiCurrencyAmount
);

export const isImmediatelySettledTotalAmountPositiveWithPreauthorizations = createSelector(
    getImmediatelySettledTotalAmountWithPreauthorizations,
    isPositiveAmount
);

export const isImmediatelySettledTotalAmountFullWithPreauthorizations = createSelector(
    getImmediatelySettledTotalAmountWithPreauthorizations,
    getReservationsTotalAmount,
    (immediatelySettled, totalAmount) => isImmediatePriceFull({ immediatelySettled, totalAmount })
);

const getImmediateSettlementsAmount = (state: ApplicationState) =>
    flow(getImmediatelySettledAmounts, (amounts) => sumAmounts(state, { amounts }));

const getImmediateReservationPrices = (state: ApplicationState, { reservationGroupId }: WithReservationGroupId) =>
    map(
        ({ totalAmount, settlements }: ReservationPrice): ImmediateReservationPrice => ({
            immediatelySettled: getImmediateSettlementsAmount(state)(settlements || []),
            totalAmount,
        }),
        getReservationPrices(state, { reservationGroupId })
    );

const getCurrentImmediateReservationPrices = composeProps(
    getImmediateReservationPrices,
    currentReservationGroupIdPropsExtender
);

const currentReservationsRequirePaymentCard = createSelector(
    getSelectedEnterprisePaymentCardRequirement,
    getCurrentImmediateReservationPrices,
    (requirement, reservationPrices) =>
        reservationPricesRequirePaymentCard(
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            requirement || BookingEnginePaymentCardRequirement.NotRequired,
            reservationPrices
        )
);

export const shouldDisplayPaymentCardForm = createSelector(
    hasSelectedEnterprisePaymentGateway,
    currentReservationsRequirePaymentCard,
    (hasPaymentGateway, reservationsRequireCard) => (hasPaymentGateway ? reservationsRequireCard : false)
);

export const hasCheckoutDocuments = (state: ApplicationState) => {
    const enterpriseId = getSelectedHotelId(state);
    const serviceId = enterpriseId != null ? getEnterpriseServiceId(state, { enterpriseId }) : null;

    if (serviceId == null) {
        return false;
    }

    return hasServiceTypesDocuments(state, {
        serviceId,
        types: CHECKOUT_DOCUMENT_TYPES,
    });
};

export const shouldCheckoutPreparationStart = overSome([
    negate(hasReservationsPrices),
    negate(isPciProxyInitialized),
    negate(hasCheckoutDocuments),
    negate(() => !isRecaptchaServiceInitialized(window)),
]);
